declare const process: { env: Record<string, string | undefined> }

export interface TokenManagerConfig {
  token?: string
  refreshToken?: string
  clientId?: string
  clientSecret?: string
  baseURL: string
  /**
   * Seconds until the caller-supplied `token` expires. Only consulted when
   * both `token` and `refreshToken` are provided (OAuth rehydrate path).
   * Defaults to 3600 — a conservative "fresh" assumption. Pass the server's
   * `expires_in` response directly if you have it; pass a small value only
   * when you know the token is near-expiry and want to force a proactive
   * refresh on the next request.
   */
  expiresIn?: number
}

interface OAuthTokenResponse {
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
}

export class TokenManager {
  private accessToken: string | undefined
  private refreshToken: string | undefined
  private expiresAt: number = 0
  private refreshPromise: Promise<void> | null = null

  constructor(private readonly config: TokenManagerConfig) {
    if (config.token !== undefined && config.token.length === 0) {
      throw new Error('token must be a non-empty string')
    }
    if (config.token && !config.refreshToken && !config.clientId) {
      this.accessToken = config.token
      this.expiresAt = Infinity
    } else if (config.token) {
      this.accessToken = config.token
      this.refreshToken = config.refreshToken
      const ttlSeconds = config.expiresIn ?? 3600
      this.expiresAt = Date.now() + ttlSeconds * 1000
    } else {
      const envPat = process.env['HOSPITABLE_API_PAT']
      if (envPat) {
        this.accessToken = envPat
        this.expiresAt = Infinity
      }
    }
  }

  async getAuthHeader(): Promise<string> {
    if (this.needsRefresh()) {
      await this.ensureRefreshed()
    }
    /* v8 ignore next 3 */
    if (!this.accessToken) {
      throw new Error('No access token available. Provide token or clientId+clientSecret.')
    }
    return `Bearer ${this.accessToken}`
  }

  private needsRefresh(): boolean {
    if (this.expiresAt === Infinity) return false
    return Date.now() >= this.expiresAt - 60_000
  }

  private async ensureRefreshed(): Promise<void> {
    if (this.refreshPromise) {
      await this.refreshPromise
      return
    }
    this.refreshPromise = this.doRefresh().finally(() => {
      this.refreshPromise = null
    })
    await this.refreshPromise
  }

  private async doRefresh(): Promise<void> {
    const { clientId, clientSecret, baseURL } = this.config
    if (!clientId || !clientSecret) {
      throw new Error('Cannot refresh token: clientId and clientSecret are required')
    }

    const body = this.refreshToken
      ? new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: this.refreshToken,
          client_id: clientId,
          client_secret: clientSecret,
        })
      : new URLSearchParams({
          grant_type: 'client_credentials',
          client_id: clientId,
          client_secret: clientSecret,
        })

    const response = await fetch(`${baseURL}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })

    if (!response.ok) {
      // Drain the body to free the socket, but do NOT embed it in the
      // thrown message: OAuth error responses can echo back credentials
      // (client_secret in error_description) or PII. Callers who log
      // errors would leak whatever the server returned.
      await response.text().catch(() => '')
      throw new Error(`Token refresh failed (${response.status})`)
    }

    const data = (await response.json()) as OAuthTokenResponse
    this.accessToken = data.access_token
    if (data.refresh_token) this.refreshToken = data.refresh_token
    this.expiresAt = Date.now() + data.expires_in * 1000
  }

  async handleUnauthorized(): Promise<void> {
    this.expiresAt = 0
    await this.ensureRefreshed()
  }
}
